import React from 'react';
import { Plus, Phone, Mail, FileText } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from '@/components/ui/button';
import { AddContactDialog } from './AddContactDialog';
import type { CustomerContact } from '../../types';

interface QuickActionsMenuProps {
  onAddContact: (type: 'address' | 'email' | 'phone', data: any) => Promise<void>;
  onAddNote: (content: string) => Promise<void>;
  onCreateWorkOrder: () => void;
  contacts: CustomerContact[];
}

export const QuickActionsMenu: React.FC<QuickActionsMenuProps> = ({
  onAddContact,
  onAddNote,
  onCreateWorkOrder,
  contacts
}) => {
  const [showAddContact, setShowAddContact] = React.useState(false);
  const [showAddNote, setShowAddNote] = React.useState(false);

  const primaryPhone = contacts.find(c => c.type === 'phone' && c.isPrimary)?.value;
  const primaryEmail = contacts.find(c => c.type === 'email' && c.isPrimary)?.value;

  return (
    <>
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="outline" size="sm">
            Quick Actions
          </Button>
        </SheetTrigger>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Quick Actions</SheetTitle>
            <SheetDescription>
              Quickly perform common actions for this customer
            </SheetDescription>
          </SheetHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-4">
              <h3 className="text-sm font-medium">Contact</h3>
              <div className="grid gap-2">
                <Button
                  variant="outline"
                  className="justify-start"
                  onClick={() => setShowAddContact(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Contact Info
                </Button>
                {primaryPhone && (
                  <Button
                    variant="outline"
                    className="justify-start"
                    onClick={() => window.location.href = `tel:${primaryPhone}`}
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call {primaryPhone}
                  </Button>
                )}
                {primaryEmail && (
                  <Button
                    variant="outline"
                    className="justify-start"
                    onClick={() => window.location.href = `mailto:${primaryEmail}`}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email {primaryEmail}
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-medium">Records</h3>
              <div className="grid gap-2">
                <Button
                  variant="outline"
                  className="justify-start"
                  onClick={onCreateWorkOrder}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Create Work Order
                </Button>
                <Button
                  variant="outline"
                  className="justify-start"
                  onClick={() => setShowAddNote(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Note
                </Button>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {showAddContact && (
        <AddContactDialog
          onClose={() => setShowAddContact(false)}
          onAdd={onAddContact}
        />
      )}

      {showAddNote && (
        <Dialog open onOpenChange={() => setShowAddNote(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Note</DialogTitle>
              <DialogDescription>
                Add a note about this customer
              </DialogDescription>
            </DialogHeader>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target as HTMLFormElement;
                const content = (form.elements.namedItem('content') as HTMLTextAreaElement).value;
                
                try {
                  await onAddNote(content);
                  setShowAddNote(false);
                } catch (error) {
                  alert(error instanceof Error ? error.message : 'Failed to add note');
                }
              }}
            >
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="content">Note Content</Label>
                  <Textarea
                    id="content"
                    name="content"
                    required
                    rows={4}
                    className="resize-none"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit">Add Note</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};